import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import api, { apiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ChevronLeft, ChevronRight, LogIn, LogOut, Banknote, CreditCard, X } from "lucide-react";

const RES_STATUS = {
  CONFIRMED: { label: "Потвърдена", bg: "bg-blue-500" },
  CHECKED_IN: { label: "Настанен", bg: "bg-emerald-600" },
  CHECKED_OUT: { label: "Напуснал", bg: "bg-gray-400" },
  CANCELLED: { label: "Отказана", bg: "bg-rose-500" },
  NO_SHOW: { label: "Не се яви", bg: "bg-orange-500" },
};
const SOURCES = ["DIRECT WEBSITE", "RECEPTION", "PHONE", "BOOKING.COM", "OTHER"];

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const emptyRes = (rooms) => ({
  first_name: "", last_name: "", phone: "", email: "", country: "", adults: 2, children: 0, babies: 0,
  arrival: iso(new Date()), departure: iso(addDays(new Date(), 1)),
  room_id: rooms[0]?.id || "", price_per_night: rooms[0]?.base_price || 0, deposit: 0, paid: 0,
  payment_method: "", source: "RECEPTION", notes: "",
});

export default function PMS() {
  const [rooms, setRooms] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [start, setStart] = useState(new Date());
  const [dlg, setDlg] = useState(null);
  const [detail, setDetail] = useState(null);
  const [payAmt, setPayAmt] = useState("");
  const [payMethod, setPayMethod] = useState("CASH");
  const DAYS = 14;

  const load = () => {
    api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
    api.get("/reservations").then((r) => setReservations(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const days = useMemo(() => Array.from({ length: DAYS }, (_, i) => iso(addDays(start, i))), [start]);

  const resForCell = (roomId, day) => reservations.find((r) =>
    r.room_id === roomId && r.status !== "CANCELLED" && r.status !== "NO_SHOW" &&
    r.arrival <= day && day < r.departure);

  const save = async () => {
    const r = dlg;
    if (!r.first_name || !r.last_name || !r.room_id) return toast.error("Име, фамилия и стая са задължителни");
    try {
      const payload = { ...r, adults: +r.adults, children: +r.children, babies: +r.babies,
        price_per_night: r.price_per_night ? +r.price_per_night : null, deposit: +r.deposit || 0, paid: +r.paid || 0 };
      if (r.id) await api.put(`/reservations/${r.id}`, payload);
      else await api.post("/reservations", payload);
      toast.success("Резервацията е записана"); setDlg(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doAction = async (path, id, ok) => {
    try { await api.post(`/reservations/${id}/${path}`); toast.success(ok); load(); setDetail(null); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = async (id, status) => {
    try { await api.post(`/reservations/${id}/status`, { op_status: status }); toast.success("Статусът е обновен"); load(); setDetail(null); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addPayment = async () => {
    if (!payAmt || +payAmt <= 0) return;
    try {
      const { data } = await api.post(`/reservations/${detail.id}/payment`, { amount: +payAmt, method: payMethod });
      toast.success("Плащането е записано"); setDetail(data); setPayAmt(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const refreshDetail = (r) => { setDetail(r); setPayAmt(""); };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-display text-4xl text-forest-deep">Календар PMS</h1><div className="gold-rule w-24 my-3" /></div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setStart(addDays(start, -7))} className="rounded-xl"><ChevronLeft /></Button>
          <Button variant="outline" onClick={() => setStart(new Date())} className="rounded-xl">Днес</Button>
          <Button variant="outline" size="icon" onClick={() => setStart(addDays(start, 7))} className="rounded-xl"><ChevronRight /></Button>
          <Button onClick={() => setDlg(emptyRes(rooms))} className="rounded-xl gap-2 bg-forest text-white" data-testid="add-reservation-btn"><Plus className="w-4 h-4" /> Резервация</Button>
        </div>
      </div>

      <div className="mt-5 bg-white rounded-2xl border border-gold/10 shadow-soft overflow-x-auto">
        <table className="border-collapse w-full" data-testid="pms-calendar">
          <thead>
            <tr>
              <th className="sticky left-0 bg-cream-muted p-2 text-left text-xs text-forest min-w-[120px] z-10">Стая</th>
              {days.map((d) => {
                const dt = new Date(d);
                const today = d === iso(new Date());
                return <th key={d} className={`p-1 text-center text-xs border-l border-gold/10 min-w-[52px] ${today ? "bg-gold/20" : "bg-cream-muted"}`}>
                  <div className="text-forest font-semibold">{dt.getDate()}</div>
                  <div className="text-[10px] text-muted-foreground">{["Нд","Пн","Вт","Ср","Чт","Пт","Сб"][dt.getDay()]}</div>
                </th>;
              })}
            </tr>
          </thead>
          <tbody>
            {rooms.map((room) => (
              <tr key={room.id} className="border-t border-gold/5">
                <td className="sticky left-0 bg-white p-2 text-sm font-medium text-forest-deep z-10">{room.number}<div className="text-[10px] text-muted-foreground">{room.room_type === "APARTMENT" ? "Апарт." : "Станд."}</div></td>
                {days.map((d) => {
                  const res = resForCell(room.id, d);
                  const isStart = res && res.arrival === d;
                  return (
                    <td key={d} className="border-l border-gold/5 p-0.5 h-12">
                      {res && (
                        <button onClick={() => refreshDetail(res)} data-testid={isStart ? `res-block-${res.id}` : undefined}
                          className={`w-full h-10 ${RES_STATUS[res.status]?.bg || "bg-blue-500"} text-white text-[10px] px-1 flex items-center overflow-hidden ${isStart ? "rounded-l-lg" : ""}`}>
                          {isStart && <span className="truncate font-medium">{res.last_name}</span>}
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-3 mt-4 text-xs">
        {Object.entries(RES_STATUS).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1"><span className={`w-3 h-3 rounded ${v.bg}`} /> {v.label}</span>
        ))}
      </div>

      {/* Reservation form */}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{dlg?.id ? "Редактиране на резервация" : "Нова резервация"}</DialogTitle></DialogHeader>
          {dlg && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Име</Label><Input value={dlg.first_name} onChange={(e) => setDlg({ ...dlg, first_name: e.target.value })} data-testid="res-first-name" /></div>
              <div><Label className="text-xs">Фамилия</Label><Input value={dlg.last_name} onChange={(e) => setDlg({ ...dlg, last_name: e.target.value })} data-testid="res-last-name" /></div>
              <div><Label className="text-xs">Телефон</Label><Input value={dlg.phone} onChange={(e) => setDlg({ ...dlg, phone: e.target.value })} /></div>
              <div><Label className="text-xs">Имейл</Label><Input value={dlg.email} onChange={(e) => setDlg({ ...dlg, email: e.target.value })} /></div>
              <div><Label className="text-xs">Държава</Label><Input value={dlg.country} onChange={(e) => setDlg({ ...dlg, country: e.target.value })} /></div>
              <div><Label className="text-xs">Стая</Label>
                <Select value={dlg.room_id} onValueChange={(v) => { const rm = rooms.find((x) => x.id === v); setDlg({ ...dlg, room_id: v, price_per_night: rm?.base_price || 0 }); }}>
                  <SelectTrigger data-testid="res-room"><SelectValue /></SelectTrigger>
                  <SelectContent>{rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.number} · {r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-2 col-span-2">
                <div><Label className="text-xs">Възрастни</Label><Input type="number" value={dlg.adults} onChange={(e) => setDlg({ ...dlg, adults: e.target.value })} /></div>
                <div><Label className="text-xs">Деца</Label><Input type="number" value={dlg.children} onChange={(e) => setDlg({ ...dlg, children: e.target.value })} /></div>
                <div><Label className="text-xs">Бебета</Label><Input type="number" value={dlg.babies} onChange={(e) => setDlg({ ...dlg, babies: e.target.value })} /></div>
              </div>
              <div><Label className="text-xs">Пристигане</Label><Input type="date" value={dlg.arrival} onChange={(e) => setDlg({ ...dlg, arrival: e.target.value })} data-testid="res-arrival" /></div>
              <div><Label className="text-xs">Заминаване</Label><Input type="date" value={dlg.departure} onChange={(e) => setDlg({ ...dlg, departure: e.target.value })} data-testid="res-departure" /></div>
              <div><Label className="text-xs">Цена/нощ (€)</Label><Input type="number" value={dlg.price_per_night} onChange={(e) => setDlg({ ...dlg, price_per_night: e.target.value })} /></div>
              <div><Label className="text-xs">Депозит (€)</Label><Input type="number" value={dlg.deposit} onChange={(e) => setDlg({ ...dlg, deposit: e.target.value })} /></div>
              <div><Label className="text-xs">Платено (€)</Label><Input type="number" value={dlg.paid} onChange={(e) => setDlg({ ...dlg, paid: e.target.value })} /></div>
              <div><Label className="text-xs">Източник</Label>
                <Select value={dlg.source} onValueChange={(v) => setDlg({ ...dlg, source: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="text-xs">Бележки</Label><Textarea value={dlg.notes} onChange={(e) => setDlg({ ...dlg, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={save} className="bg-forest text-white rounded-xl" data-testid="save-reservation">Запази</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reservation detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {detail.first_name} {detail.last_name}
                  <span className={`text-xs text-white px-2 py-0.5 rounded ${RES_STATUS[detail.status]?.bg}`}>{RES_STATUS[detail.status]?.label}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-2 text-forest-deep">
                  <div>Стая: <b>{detail.room_number}</b> · {detail.room_name}</div>
                  <div>{detail.nights} нощувки</div>
                  <div>{detail.arrival} → {detail.departure}</div>
                  <div>{detail.adults} възр · {detail.children} деца · {detail.babies} бебета</div>
                  <div>Телефон: {detail.phone || "—"}</div>
                  <div>Източник: {detail.source}</div>
                </div>
                <div className="gold-rule my-2" />
                <div className="bg-cream-muted rounded-xl p-3 space-y-1">
                  <div className="flex justify-between"><span>Обща цена</span><b>{money(detail.total_price)}</b></div>
                  <div className="flex justify-between"><span>Платено</span><b className="text-emerald-700">{money(detail.paid)}</b></div>
                  <div className="flex justify-between"><span>Остатък</span><b className="text-rose-700" data-testid="res-remaining">{money(detail.remaining ?? (detail.total_price - detail.paid))}</b></div>
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1"><Label className="text-xs">Плащане (€)</Label><Input type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} data-testid="hotel-pay-amount" /></div>
                  <Select value={payMethod} onValueChange={setPayMethod}>
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="CASH">В брой</SelectItem><SelectItem value="CARD">С карта</SelectItem></SelectContent>
                  </Select>
                  <Button onClick={addPayment} className="bg-gold text-forest-deep rounded-xl" data-testid="hotel-add-payment">Запиши</Button>
                </div>
                {detail.notes && <div className="text-xs text-muted-foreground">Бележки: {detail.notes}</div>}
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" onClick={() => { setDlg({ ...detail, price_per_night: detail.price_per_night }); setDetail(null); }} className="rounded-xl">Редактирай</Button>
                {detail.status === "CONFIRMED" && <Button onClick={() => doAction("checkin", detail.id, "Настаняване завършено")} className="bg-emerald-600 text-white rounded-xl gap-1" data-testid="checkin-btn"><LogIn className="w-4 h-4" /> Настани</Button>}
                {detail.status === "CHECKED_IN" && <Button onClick={() => doAction("checkout", detail.id, "Напускане завършено")} className="bg-forest text-white rounded-xl gap-1" data-testid="checkout-btn"><LogOut className="w-4 h-4" /> Напусни</Button>}
                {["CONFIRMED"].includes(detail.status) && <Button variant="outline" onClick={() => setStatus(detail.id, "CANCELLED")} className="rounded-xl text-destructive">Отказ</Button>}
                {["CONFIRMED"].includes(detail.status) && <Button variant="outline" onClick={() => setStatus(detail.id, "NO_SHOW")} className="rounded-xl">Не се яви</Button>}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
